import React from 'react';

import Store from 'store';
import Session from 'store/session';
import { getProfilePath } from 'common';

const ImportContext = React.createContext();

function embedSnapshot(embed) {
  const snapshot = {
    initial: embed,
    type: embed.type,
  };
  if (embed.type === 'equipment') {
    snapshot.class = embed.class;
    snapshot.items = embed.items;
  }
  if (embed.type === 'skills') {
    snapshot.class = embed.class;
    snapshot.levels = embed.levels;
    snapshot.data = embed.data;
    snapshot.history = embed.history;
  }
  if (embed.type === 'cards') {
    snapshot.cards = embed.cards;
  }
  return snapshot;
}

function embedEquals(embed, snapshot) {
  if (embed.type !== snapshot.type) return false;

  if (embed.type === 'equipment') {
    if (embed.class !== snapshot.class) return false;
    if (embed.items !== snapshot.items) return false;
  }
  if (embed.type === 'skills') {
    if (embed.class !== snapshot.class) return false;
    if (embed.levels !== snapshot.levels) return false;
    if (embed.data !== snapshot.data) return false;
    if (embed.history !== snapshot.history) return false;
  }
  if (embed.type === 'cards') {
    if (embed.cards !== snapshot.cards) return false;
  }

  return true;
}

function stateSnapshot(state) {
  return {
    initial: state,
    imported: state.importedId,
    embeds: state.embeds.map(embed => embedSnapshot(embed)),
    builder: state.builder,
  };
}
function stateEquals(state, snapshot) {
  if (state.embeds.length !== snapshot.embeds?.length) return false;
  return state.embeds.every((embed, index) => embedEquals(embed, snapshot.embeds[index])) && state.builder === snapshot.builder;
}

export function useImportId() {
  return React.useContext(ImportContext);
}

export function ImportTracker({ children, builder }) {
  const [imported, setImported] = React.useState();

  const store = Store.useStore();
  const dispatch = Store.useDispatch();

  const userId = Session.useSelector(state => state.userId);

  const importedRef = React.useRef();
  const snapshotRef = React.useRef({});

  React.useEffect(() => {
    function update() {
      const state = store.getState();

      const snapshots = snapshotRef.current;
      const newSnapshots = snapshotRef.current = {};
  
      let demoteState = false, modifiedState = false;
      if (state.imported) {
        if (!snapshots[state.id] || snapshots[state.id].imported !== state.importedId) {
          newSnapshots[state.id] = stateSnapshot(state);
        } else if (stateEquals(state, snapshots[state.id])) {
          newSnapshots[state.id] = snapshots[state.id];
          if (state.imported !== 'modified' && state !== snapshots[state.id].initial) {
            if (snapshots[state.id].initial) {
              modifiedState = true;
              snapshots[state.id].initial = null;
            } else {
              snapshots[state.id].initial = state;
            }
          }
        } else {
          demoteState = true;
        }
      }
  
      function updateUrl(data, hash) {
        const id = (data?.imported && data.importedId) || null;
        if (importedRef.current !== id) {
          importedRef.current = id;
          setImported(id);
        }
  
        const url = new URL(getProfilePath(id || '', builder), window.location);
        if (window.location.search) url.search = window.location.search;
        if (!id && data?.importedId) {
          url.hash = '#' + data.importedId;
        } else if (hash) {
          url.hash = hash;
        }
        if (url.href !== window.location.href) {
          window.history?.replaceState(null, '', url.href);
        }
      }
      
      if (state.imported && !demoteState) {
        const active = state.embeds[state.activeEmbed];
        updateUrl(state, active && `#${active.id}`);
      } else if (state.importedId && userId && state.user === userId) {
        updateUrl(state);
      } else {
        updateUrl();
      }
  
      if (demoteState || modifiedState) {
        dispatch.setState(state => {
          state = {...state};
          if (demoteState) state.imported = false;
          else if (modifiedState) state.imported = 'modified';
          return state;
        });
      }
    }
    update();
    return store.subscribe(update);
  }, [store, dispatch, userId, builder]);

  return (
    <ImportContext.Provider value={imported}>
      {children}
    </ImportContext.Provider>
  );
}
